import { useState, useEffect } from 'react';
import { useParams, useNavigate, useSearchParams } from 'react-router-dom';
import { useAuthContext } from '../../contexts/AuthContext';

const API_BASE = import.meta.env.VITE_API_URL || '';
const FONT = '"Geist", system-ui, sans-serif';

/**
 * Invite acceptance states — each maps to an honest, user-comprehensible screen.
 * No silent redirects on failure.
 */
type InviteState =
  | 'loading'           // Checking auth or sending accept
  | 'unauthenticated'   // Not logged in
  | 'accepted'          // Just accepted, redirecting
  | 'already_accepted'  // Already a collaborator, redirecting
  | 'owner'             // User owns the doc, redirecting
  | 'wrong_account'     // Logged in with wrong email
  | 'invalid'           // No invite exists
  | 'error';            // Generic failure

export default function SharedInvitePage() {
  const { conversationId } = useParams<{ conversationId: string }>();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const { loading: authLoading, user, getToken } = useAuthContext();
  const [state, setState] = useState<InviteState>('loading');
  const [userEmail, setUserEmail] = useState<string | null>(null);

  useEffect(() => {
    if (authLoading) return;
    if (!user) { setState('unauthenticated'); return; }
    if (!conversationId) { setState('invalid'); return; }

    const accept = async () => {
      setState('loading');
      try {
        const token = getToken();
        console.log('[SharedInvite] Accepting invite for conversation:', conversationId, 'hasToken:', !!token);
        const res = await fetch(`${API_BASE}/api/sharing/${conversationId}/accept`, {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            ...(token ? { Authorization: `Bearer ${token}` } : {}),
            ...(searchParams.get('guestId') ? { 'x-corner-guest-id': searchParams.get('guestId')! } : {}),
          },
        });

        const body = await res.json().catch(() => ({}));
        const status = body.status as string | undefined;
        console.log('[SharedInvite] Accept response:', { status, httpStatus: res.status, body });

        if (status === 'accepted' || status === 'already_accepted' || status === 'owner') {
          setState(status as InviteState);
          const target = `/app/conversations/${conversationId}`;
          console.log('[SharedInvite] Redirecting to:', target);
          setTimeout(() => navigate(target, { replace: true }), 800);
          return;
        }
        if (status === 'wrong_account') {
          setUserEmail(body.userEmail ?? null);
          setState('wrong_account');
          return;
        }
        if (status === 'invalid' || res.status === 404) {
          setState('invalid');
          return;
        }
        setState('error');
      } catch {
        setState('error');
      }
    };

    accept();
  }, [authLoading, user, getToken, conversationId, navigate, searchParams]);

  // ─── Loading ───────────────────────────────────
  if (state === 'loading') {
    return (
      <div style={centerStyle}>
        <p style={{ margin: 0, fontSize: 14, color: '#9E9189', fontFamily: FONT }}>
          {authLoading ? 'Checking your account...' : 'Joining document...'}
        </p>
      </div>
    );
  }

  // ─── Success — brief confirmation then redirect ─
  if (state === 'accepted' || state === 'already_accepted' || state === 'owner') {
    return (
      <div style={centerStyle}>
        <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 8 }}>
          <svg width={24} height={24} viewBox="0 0 24 24" fill="none" stroke="#4A9E6B" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
            <polyline points="20 6 9 17 4 12" />
          </svg>
          <p style={{ margin: 0, fontSize: 15, fontWeight: 500, color: '#3D3229', fontFamily: FONT }}>
            {state === 'owner' ? 'This is your document' : state === 'already_accepted' ? 'You already have access' : 'Invitation accepted'}
          </p>
          <p style={{ margin: 0, fontSize: 13, color: '#9E9189', fontFamily: FONT }}>Redirecting...</p>
        </div>
      </div>
    );
  }

  // ─── Unauthenticated — sign up / log in ────────
  if (state === 'unauthenticated') {
    const guestId = searchParams.get('guestId');
    const invitePath = `/app/shared/invite/${conversationId}${guestId ? `?guestId=${encodeURIComponent(guestId)}` : ''}`;
    const redirectParam = encodeURIComponent(invitePath);
    return (
      <div style={pageStyle}>
        <TopBar />
        <div style={contentCenterStyle}>
          <div style={cardStyle}>
            <div style={iconCircleStyle}>
              <svg width="28" height="28" viewBox="0 0 24 24" fill="none" stroke="#D97645" strokeWidth="1.5" strokeLinecap="round" style={{ flexShrink: 0 }}>
                <path d="M4 12v8a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2v-8" />
                <polyline points="16 6 12 2 8 6" />
                <line x1="12" y1="2" x2="12" y2="15" />
              </svg>
            </div>
            <h1 style={h1Style}>You've been invited</h1>
            <p style={descStyle}>Someone shared a document with you on Corner. Log in or create an account to view and collaborate.</p>
            <div style={btnGroupStyle}>
              <button onClick={() => navigate(`/auth/register?redirect=${redirectParam}`)} style={primaryButtonStyle}>Sign up free</button>
              <button onClick={() => navigate(`/auth/login?redirect=${redirectParam}`)} style={secondaryButtonStyle}>I already have an account</button>
            </div>
          </div>
        </div>
      </div>
    );
  }

  // ─── Error states — honest messages ────────────
  const errorCfg: Record<string, { title: string; msg: string; showSignIn?: boolean }> = {
    wrong_account: {
      title: 'Wrong account',
      msg: userEmail
        ? `You're signed in as ${userEmail}, but this invite was sent to a different email. Sign in with the invited email to accept.`
        : `You're signed in with a different email than the one this invite was sent to.`,
      showSignIn: true,
    },
    invalid: {
      title: 'Invite not found',
      msg: 'This invite link is no longer valid. It may have been revoked or the document is no longer shared.',
    },
    error: {
      title: 'Something went wrong',
      msg: 'We couldn\'t process this invitation. Please try again or contact the person who shared the document.',
    },
  };
  const cfg = errorCfg[state] ?? errorCfg.error;

  return (
    <div style={pageStyle}>
      <TopBar />
      <div style={contentCenterStyle}>
        <div style={cardStyle}>
          <div style={{ ...iconCircleStyle, background: '#FFF3EE' }}>
            <svg width="28" height="28" viewBox="0 0 24 24" fill="none" stroke="#C04A2A" strokeWidth="1.5" strokeLinecap="round" style={{ flexShrink: 0 }}>
              <circle cx="12" cy="12" r="10" />
              <line x1="15" y1="9" x2="9" y2="15" />
              <line x1="9" y1="9" x2="15" y2="15" />
            </svg>
          </div>
          <h1 style={{ ...h1Style, fontSize: 20 }}>{cfg.title}</h1>
          <p style={descStyle}>{cfg.msg}</p>
          <div style={btnGroupStyle}>
            {cfg.showSignIn && <button onClick={() => navigate('/auth/login')} style={primaryButtonStyle}>Sign in with a different account</button>}
            <button onClick={() => navigate('/app')} style={secondaryButtonStyle}>Go to Corner</button>
          </div>
        </div>
      </div>
    </div>
  );
}

function TopBar() {
  return (
    <div style={topBarStyle}>
      <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
        <svg width="24" height="24" viewBox="0 0 256 256" fill="none" style={{ flexShrink: 0 }}>
          <defs><linearGradient id="inviteLogo" x1="42" y1="38.5" x2="207" y2="209" gradientUnits="userSpaceOnUse"><stop stopColor="#D46A3A" /><stop offset="1" stopColor="#8B3A10" /></linearGradient></defs>
          <path d="M236 152V32C236 25.373 230.627 20 224 20H32C25.373 20 20 25.373 20 32L20 224C20 230.627 25.373 236 32 236H152" stroke="url(#inviteLogo)" strokeWidth="40" strokeLinecap="round" />
        </svg>
        <span style={{ fontSize: 14, fontWeight: 600, color: '#3D3229', fontFamily: FONT }}>Corner</span>
      </div>
    </div>
  );
}

// ─── Styles ─────────────────────────────────────

const centerStyle: React.CSSProperties = {
  display: 'flex', alignItems: 'center', justifyContent: 'center',
  height: '100vh', width: '100vw', background: '#F5F0EB', fontFamily: FONT,
};

const pageStyle: React.CSSProperties = {
  display: 'flex', flexDirection: 'column', height: '100vh', width: '100vw',
  background: '#F5F0EB', fontFamily: FONT,
};

const topBarStyle: React.CSSProperties = {
  display: 'flex', alignItems: 'center', padding: '0 20px', height: 52,
  borderBottom: '1px solid #E5DDD5', background: '#fff', flexShrink: 0,
};

const cardStyle: React.CSSProperties = {
  display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 14,
  padding: '40px 44px', background: '#fff', borderRadius: 16,
  border: '1px solid #E5DDD5', maxWidth: 400, width: '100%',
  boxShadow: '0 4px 24px rgba(0,0,0,0.06)',
};

const iconCircleStyle: React.CSSProperties = {
  width: 56, height: 56, borderRadius: '50%', display: 'flex',
  alignItems: 'center', justifyContent: 'center',
  background: '#FFF3EB', marginBottom: 4,
};

const contentCenterStyle: React.CSSProperties = {
  flex: 1, display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 20,
};

const h1Style: React.CSSProperties = {
  margin: 0, fontSize: 22, fontWeight: 700, color: '#3D3229', fontFamily: FONT, textAlign: 'center',
};

const descStyle: React.CSSProperties = {
  margin: 0, fontSize: 14, color: '#9E9189', fontFamily: FONT, textAlign: 'center', lineHeight: '20px',
};

const btnGroupStyle: React.CSSProperties = {
  display: 'flex', flexDirection: 'column', gap: 10, width: '100%', marginTop: 8,
};

const primaryButtonStyle: React.CSSProperties = {
  background: '#1A1714', border: 'none', borderRadius: 10, cursor: 'pointer',
  padding: '12px 0', fontSize: 15, fontWeight: 600, color: '#fff', fontFamily: FONT,
  width: '100%',
};

const secondaryButtonStyle: React.CSSProperties = {
  background: 'none', border: '1px solid #E5DDD5', borderRadius: 10, cursor: 'pointer',
  padding: '12px 0', fontSize: 15, fontWeight: 500, color: '#3D3229', fontFamily: FONT,
  width: '100%',
};
